"use strict";
/**
 * Recent view digest for multi-device fan-out via history focus SSE.
 *
 * Authority client (just refreshed PHP recent + focus) publishes a snapshot
 * on the focus history payload. Peers merge by note ID without hitting PHP recent.
 *
 * Policy (dogfood, until Recent itself is simplified):
 * - Digest **rows match Recent row shape** (same fields peers need to paint the list).
 * - Still top-N only (limit), not the entire history DB.
 * - WAF exclusions cover free-form note text on `/api/v2/history/sync` — do not
 *   invent a parallel “control-only” schema until Recent is redesigned.
 * - Client-only UI flags (edit state, $$hashKey, pills cache) are stripped.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.extractRecentDigestFromFocusData = exports.isRecentDigestFresher = exports.mergeRecentDigestById = exports.buildRecentViewDigest = exports.RECENT_DIGEST_DEFAULT_LIMIT = void 0;
exports.RECENT_DIGEST_DEFAULT_LIMIT = 8;
/** Mirror Recent — full allowlist of server-ish row fields. */
var ROW_KEYS = [
    'ID',
    'pnParentFK',
    'pnTaskGroup',
    'pnTopicGroup',
    'pnTopicDetail',
    'pnFlowKind',
    'pnBody',
    'pnAccount',
    'pnUserRealm',
    'pnAssetAccount',
    'pnAssetRealm',
    'pnAssetTag',
    'pnAssetTagAux',
    'pnCustomTimeUTC',
    'pnCompletedUTC',
    'pnCompletedPersonPK',
    'pnInvoiceNote',
    'pnValue',
    'pnSchedule',
    'ItemCodePath',
    'FilterString',
    'LocalTime',
    'Duration',
];
/**
 * Keys that must never leave the local tab (Angular / ephemeral UI).
 * Everything else on the row that is in ROW_KEYS is copied as-is.
 */
var CLIENT_ONLY_ROW_KEYS = new Set([
    '$$hashKey',
    'edit',
    'reply',
    'viewExtra',
    'threadPills',
    'localOnly',
    'encryptionState',
    'draft',
    'isOpen',
    'selected',
]);
function compactRow(row) {
    if (!row || row.ID == null || row.ID === '')
        return null;
    var out = { ID: row.ID };
    for (var _i = 0, ROW_KEYS_1 = ROW_KEYS; _i < ROW_KEYS_1.length; _i++) {
        var key = ROW_KEYS_1[_i];
        if (key === 'ID')
            continue;
        if (CLIENT_ONLY_ROW_KEYS.has(key))
            continue;
        if (row[key] === undefined)
            continue;
        out[key] = row[key];
    }
    if (out.pnFlowKind != null && String(out.pnFlowKind).trim() !== '') {
        out.pnFlowKind = String(out.pnFlowKind).trim().toUpperCase();
    }
    return out;
}
/**
 * Build a digest from a full Recent payload (+ optional focus meta).
 * Row field set mirrors Recent; meta passed through unchanged.
 */
function buildRecentViewDigest(rows, opts) {
    var _a;
    if (!Array.isArray(rows) || rows.length === 0) {
        return null;
    }
    var limit = Math.max(1, Math.min(20, (_a = opts === null || opts === void 0 ? void 0 : opts.limit) !== null && _a !== void 0 ? _a : exports.RECENT_DIGEST_DEFAULT_LIMIT));
    var compact = [];
    for (var i = 0; i < rows.length && compact.length < limit; i++) {
        var c = compactRow(rows[i]);
        if (c)
            compact.push(c);
    }
    if (compact.length === 0) {
        return null;
    }
    var digest = {
        at: typeof (opts === null || opts === void 0 ? void 0 : opts.at) === 'number' && opts.at > 0 ? opts.at : Date.now(),
        rows: compact,
    };
    // Pass Focus meta through as Recent/Focus payload did (openId, bodyPreview, etc.)
    if ((opts === null || opts === void 0 ? void 0 : opts.openMeta) !== undefined)
        digest.openMeta = opts.openMeta;
    if ((opts === null || opts === void 0 ? void 0 : opts.sessionMeta) !== undefined)
        digest.sessionMeta = opts.sessionMeta;
    if ((opts === null || opts === void 0 ? void 0 : opts.idleMeta) !== undefined)
        digest.idleMeta = opts.idleMeta;
    if (opts === null || opts === void 0 ? void 0 : opts.deviceId)
        digest.deviceId = opts.deviceId;
    return digest;
}
exports.buildRecentViewDigest = buildRecentViewDigest;
/**
 * Merge digest rows into the current recent list by ID.
 * Digest order wins for the top slice; other local rows follow (unchanged).
 */
function mergeRecentDigestById(current, digestRows) {
    var cur = Array.isArray(current) ? current.slice() : [];
    if (!Array.isArray(digestRows) || digestRows.length === 0) {
        return cur;
    }
    var byId = new Map();
    for (var _i = 0, cur_1 = cur; _i < cur_1.length; _i++) {
        var r = cur_1[_i];
        if (r && r.ID != null) {
            byId.set(String(r.ID), r);
        }
    }
    var merged = [];
    var seen = new Set();
    for (var _a = 0, digestRows_1 = digestRows; _a < digestRows_1.length; _a++) {
        var d = digestRows_1[_a];
        if (!d || d.ID == null)
            continue;
        var id = String(d.ID);
        var prev = byId.get(id);
        if (prev) {
            // Overlay digest fields; keep local-only keys not present on digest
            merged.push(Object.assign({}, prev, d));
        }
        else {
            merged.push(Object.assign({}, d));
        }
        seen.add(id);
    }
    for (var _b = 0, cur_2 = cur; _b < cur_2.length; _b++) {
        var r = cur_2[_b];
        if (!r || r.ID == null)
            continue;
        var id = String(r.ID);
        if (!seen.has(id)) {
            merged.push(r);
        }
    }
    return merged;
}
exports.mergeRecentDigestById = mergeRecentDigestById;
/** True when digest is fresh enough to apply over local recentLastAt. */
function isRecentDigestFresher(digestAt, localRecentLastAt, skewMs) {
    if (skewMs === void 0) { skewMs = 2000; }
    if (digestAt == null || !Number.isFinite(digestAt)) {
        return true;
    }
    if (localRecentLastAt == null || !Number.isFinite(localRecentLastAt)) {
        return true;
    }
    return digestAt + skewMs >= localRecentLastAt;
}
exports.isRecentDigestFresher = isRecentDigestFresher;
function extractRecentDigestFromFocusData(data) {
    if (!data || typeof data !== 'object')
        return null;
    var dig = data.recentDigest;
    if (!dig || typeof dig !== 'object')
        return null;
    if (!Array.isArray(dig.rows) || dig.rows.length === 0)
        return null;
    return dig;
}
exports.extractRecentDigestFromFocusData = extractRecentDigestFromFocusData;
